import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {HubspotService} from '../services/enrich/hubspot.service';
import {ClientService} from '../services/auth/client.service';
import {EnrichReadinessContext, EnrichSetupService, isEnrichRuntimeUnavailableError, isLocalEnrichSearchRoute} from '../services/enrich/enrich-setup.service';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {ConnectConnectionsService} from '../services/connect/connect-connections.service';
import {HubspotReadinessResponse} from '../models/hubspot.model';

@Injectable({
  providedIn: 'root'
})
export class EnrichSetupGuard implements CanActivate {
    private hubspotService = inject(HubspotService);
    private clientService = inject(ClientService);
    private enrichSetupService = inject(EnrichSetupService);
    private router = inject(Router);
    private connectConnectionsService = inject(ConnectConnectionsService);


  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    // Setup is a repairable health checklist and must remain reachable even after completion.
    if (state.url.includes('/enrich/setup')) {
      return of(true);
    }

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      // If no client is selected, allow access (client selection will be handled by the component)
      return of(true);
    }

    return this.hubspotService.getSettings(clientId).pipe(
      switchMap(settings => forkJoin({
        context: this.resolveConnectionContext(settings.hubspotConnectionId),
        backendReadiness: this.resolveBackendReadiness(clientId),
      }).pipe(
        map(({context, backendReadiness}) => {
          const readiness = this.enrichSetupService.updateReadinessFromSettings(settings, {
            ...context,
            backendReadiness,
          });
          if (readiness.localSearchOnly) {
            if (isLocalEnrichSearchRoute(state.url)) return true;
            this.router.navigate(['/enrich/companies']);
            return false;
          }
          if (readiness.isOperationalReady) {
            return true;
          }

          if ((state.url.includes('/enrich/mapping') || state.url.includes('/enrich/settings')) && route.queryParams['setup'] === 'true') {
            return true;
          }

          this.router.navigate(['/enrich/setup']);
          return false;
        })
      )),
      catchError(error => {
        this.enrichSetupService.updateReadinessFromSettings(null, {
          runtimeUnavailable: isEnrichRuntimeUnavailableError(error),
        });
        this.router.navigate(['/enrich/setup']);
        return of(false);
      })
    );
  }

  private resolveConnectionContext(connectionId: string | null | undefined): Observable<EnrichReadinessContext> {
    const selectedConnectionId = String(connectionId || '').trim();
    if (!selectedConnectionId) {
      return of({selectedConnectionId: null});
    }

    return this.connectConnectionsService.getPermissions(selectedConnectionId, {service: 'shine-enrich'}).pipe(
      take(1),
      map(connectionPermissions => ({
        selectedConnectionId,
        connectionPermissions,
        authStatus: {authenticated: connectionPermissions.connected !== false},
      })),
      catchError(() => of({
        selectedConnectionId,
        connectionPermissionError: 'Unable to verify HubSpot connection permissions.',
      })),
    );
  }

  private resolveBackendReadiness(clientId: string): Observable<HubspotReadinessResponse | null> {
    return this.hubspotService.getReadiness(clientId).pipe(
      take(1),
      catchError(() => of(null)),
    );
  }
}
